import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api.js';
import type { PoS, PoSDistributor } from '../../types/index.js';
import { DistributorSelector } from '../customers/DistributorSelector.js';
import {
  InvoiceBillingSection,
  invoiceAddressPayload,
  hasStoredInvoiceAddress,
} from '../customers/InvoiceBillingSection.js';
import { StoreTypeSelector } from './StoreTypeSelector.js';
import { AddressForm } from '../shared/AddressForm.js';

const editSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  phone: z.string().optional(),
  email: z.string().email().optional().or(z.literal('')),
  invoice_email: z.string().email().optional().or(z.literal('')),
  contact_person_name: z.string().optional(),
  contact_person_position: z.string().optional(),
  contact_person_phone: z.string().optional(),
  contact_person_email: z.string().email().optional().or(z.literal('')),
  contact_availability_time: z.string().optional(),
  notes: z.string().optional(),
  owner_name: z.string().optional(),
  invoice_name: z.string().optional(),
  invoice_street: z.string().optional(),
  invoice_street_number: z.string().optional(),
  invoice_postal_code: z.string().optional(),
  invoice_city: z.string().optional(),
  invoice_country: z.string().optional(),
  store_type: z.string().optional(),
  street: z.string().optional(),
  street_number: z.string().optional(),
  postal_code: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
});

type EditValues = z.infer<typeof editSchema>;

interface DistributorLink {
  distributor_id: string;
  pos_number_at_distributor: string;
  is_primary: boolean;
}

interface EditPoSSheetProps {
  open: boolean;
  pos: PoS;
  onClose: () => void;
  onSaved: (pos: PoS) => void;
}

function valuesFromPos(pos: PoS): EditValues {
  return {
    name: pos.name || '',
    phone: pos.phone || '',
    email: pos.email || '',
    invoice_email: pos.invoice_email || '',
    contact_person_name: pos.contact_person_name || '',
    contact_person_position: pos.contact_person_position || '',
    contact_person_phone: pos.contact_person_phone || '',
    contact_person_email: pos.contact_person_email || '',
    contact_availability_time: pos.contact_availability_time || '',
    notes: pos.notes || '',
    owner_name: pos.owner_name || '',
    invoice_name: pos.invoice_name || '',
    invoice_street: pos.invoice_street || '',
    invoice_street_number: pos.invoice_street_number || '',
    invoice_postal_code: pos.invoice_postal_code || '',
    invoice_city: pos.invoice_city || '',
    invoice_country: pos.invoice_country || '',
    store_type: pos.store_type || '',
    street: pos.street || '',
    street_number: pos.street_number || '',
    postal_code: pos.postal_code || '',
    city: pos.city || '',
    country: pos.country || 'Germany',
  };
}

function toPayload(data: EditValues) {
  return {
    name: data.name,
    phone: data.phone || undefined,
    email: data.email || undefined,
    invoice_email: data.invoice_email || undefined,
    contact_person_name: data.contact_person_name || undefined,
    contact_person_position: data.contact_person_position || undefined,
    contact_person_phone: data.contact_person_phone || undefined,
    contact_person_email: data.contact_person_email || undefined,
    contact_availability_time: data.contact_availability_time || undefined,
    notes: data.notes || undefined,
    owner_name: data.owner_name || undefined,
    ...invoiceAddressPayload(data),
    store_type: data.store_type || undefined,
    street: data.street || undefined,
    street_number: data.street_number || undefined,
    postal_code: data.postal_code || undefined,
    city: data.city || undefined,
    country: data.country || undefined,
  };
}

async function syncDistributorLinks(
  posId: string,
  next: DistributorLink[],
  initial: DistributorLink[]
): Promise<void> {
  for (const link of next) {
    await api.post('/distributors/links', {
      pos_id: posId,
      distributor_id: link.distributor_id,
      pos_number_at_distributor: link.pos_number_at_distributor || '',
      is_primary: link.is_primary,
    });
  }
  const keep = new Set(next.map((l) => l.distributor_id));
  for (const link of initial) {
    if (!keep.has(link.distributor_id)) {
      await api.delete(`/distributors/links/${posId}/${link.distributor_id}`);
    }
  }
}

export const EditPoSSheet: React.FC<EditPoSSheetProps> = ({
  open,
  pos,
  onClose,
  onSaved,
}) => {
  const [saving, setSaving] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [links, setLinks] = useState<DistributorLink[]>([]);
  const [initialLinks, setInitialLinks] = useState<DistributorLink[]>([]);
  const form = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: valuesFromPos(pos),
  });

  useEffect(() => {
    if (!open) return;
    form.reset(valuesFromPos(pos));
    setShowMore(false);
    void api
      .get<PoSDistributor[]>(`/pos/${pos.id}/distributors`)
      .then((res) => {
        const mapped = (res.data || []).map((row) => ({
          distributor_id: row.distributor_id,
          pos_number_at_distributor: row.pos_number_at_distributor || '',
          is_primary: row.is_primary,
        }));
        setLinks(mapped);
        setInitialLinks(mapped);
      })
      .catch(() => toast.error('Failed to load distributors'));
  }, [open, pos, form]);

  const onSubmit = async (data: EditValues) => {
    setSaving(true);
    try {
      const res = await api.put<PoS>(`/pos/${pos.id}`, toPayload(data));
      await syncDistributorLinks(pos.id, links, initialLinks);
      toast.success('PoS updated');
      onSaved(res.data);
      onClose();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } }).response?.data
          ?.error || 'Failed to update PoS';
      toast.error(typeof message === 'string' ? message : 'Failed to update PoS');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-50" />
        <Dialog.Content
          className="fixed inset-x-0 bottom-0 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2
            md:-translate-y-1/2 bg-white rounded-t-2xl md:rounded-2xl shadow-2xl w-full max-w-lg
            max-h-[92vh] overflow-y-auto z-50 p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-lg font-black text-gray-900">
              Edit PoS
            </Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" className="p-2 text-gray-400" aria-label="Close">
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <CoreFields form={form} />
            <InvoiceBillingSection
              register={form.register}
              errors={form.formState.errors}
              setValue={form.setValue}
              watch={form.watch}
              initialDifferentAddress={hasStoredInvoiceAddress(pos)}
            />
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-2">Distributors</h3>
              <DistributorSelector value={links} onChange={setLinks} />
            </div>
            <button
              type="button"
              onClick={() => setShowMore((v) => !v)}
              className="text-sm font-bold text-brand-700 underline"
            >
              {showMore ? 'Hide address & store type' : 'More: address & store type'}
            </button>
            {showMore && (
              <div className="space-y-4 border-t border-gray-200 pt-4">
                <StoreTypeSelector
                  value={form.watch('store_type') || ''}
                  onChange={(value) => form.setValue('store_type', value)}
                />
                <AddressForm
                  register={form.register}
                  errors={form.formState.errors}
                  required={false}
                />
              </div>
            )}
            <button
              type="submit"
              disabled={saving}
              className="w-full min-h-[48px] py-3 bg-brand-600 text-white rounded-xl font-bold disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

function CoreFields({
  form,
}: {
  form: ReturnType<typeof useForm<EditValues>>;
}) {
  const { register, formState: { errors }, watch, setValue } = form;
  return (
    <div className="space-y-3">
      <Field label="Store name" error={errors.name?.message}>
        <input {...register('name')} className={inputClass} />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Phone">
          <input type="tel" {...register('phone')} className={inputClass} />
        </Field>
        <Field label="Email" error={errors.email?.message}>
          <input type="email" {...register('email')} className={inputClass} />
        </Field>
      </div>
      <Field label="Contact person">
        <input {...register('contact_person_name')} className={inputClass} />
      </Field>
      <Field label="Position">
        <input {...register('contact_person_position')} className={inputClass} />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Contact phone">
          <input type="tel" {...register('contact_person_phone')} className={inputClass} />
        </Field>
        <Field label="Contact email" error={errors.contact_person_email?.message}>
          <input type="email" {...register('contact_person_email')} className={inputClass} />
        </Field>
      </div>
      <AvailabilityTimes
        value={watch('contact_availability_time') || ''}
        onChange={(value) => setValue('contact_availability_time', value)}
      />
      <Field label="Notes">
        <textarea {...register('notes')} rows={3} className={inputClass} />
      </Field>
    </div>
  );
}

const inputClass =
  'w-full px-3 py-2.5 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-brand-500';

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

function AvailabilityTimes({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const selected = value.split(',').filter(Boolean);
  const options = [
    { value: 'morning', label: 'Morning' },
    { value: 'afternoon', label: 'Afternoon' },
    { value: 'evening', label: 'Evening' },
  ];
  return (
    <div>
      <p className="text-sm font-semibold text-gray-700 mb-1">Best time</p>
      <div className="flex gap-2">
        {options.map((opt) => {
          const on = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                const next = on
                  ? selected.filter((v) => v !== opt.value)
                  : [...selected, opt.value];
                onChange(next.join(','));
              }}
              className={`flex-1 min-h-[44px] rounded-lg text-sm font-semibold border-2 ${
                on
                  ? 'bg-blue-500 text-white border-blue-600'
                  : 'bg-gray-50 text-gray-700 border-gray-200'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
